import * as typeformEmbed from "@typeform/embed";
import {
  useModalContext,
  WSButton,
  WSButtons,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSImage,
  WSMessageBox,
  WSText
} from "@wingspanhq/fe-component-library";
import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { INewUser, SessionType } from "@wingspanhq/users/dist/lib/interfaces";
import { formatMoney } from "accounting";
import React from "react";
import { queryCache } from "react-query";
import { RouteComponentProps, useHistory } from "react-router-dom";
import handsUpImage from "../../assets/icons/hands_up.svg";
import { Tooltip } from "../../components/Tooltip";
import {
  ModalReferrals,
  REFERRALS_MODAL
} from "../../Dashboard/screens/ModalReferrals";
import { useDownloadPdf } from "../../Invoices/utils";
import { useUserId } from "../../query/hooks/helpers";
import { QUERY_CLIENT_INVOICE } from "../../query/payments/keys";
import { useUpdateClientInvoice } from "../../query/payments/mutations";
import { useClientInvoiceQuery } from "../../query/payments/queries";
import { getClientInvoiceMemberName } from "../../query/payments/selectors";
import { useSession } from "../../query/session";
import { useMemberProfile, useUserProfile } from "../../query/users/queries";
import {
  getSegmentAnonymousIdFromLocalStorage,
  track
} from "../../utils/analytics";
import { Card } from "../components/Card/Card";
import { ClientInvoiceWrapper } from "../components/ClientInvoiceWrapper/ClientInvoiceWrapper";
import { CLIENT_SIGN_IN_MODAL_KEY } from "../components/ClientSignInModal/ClientSignInModal";
import { Header } from "../components/Header/Header";
import { InfoRow } from "../components/InfoRow/InfoRow";
import { List } from "../components/List/List";
import styles from "./ClientPaymentsInvoicePaymentSuccess.module.scss";

type Props = RouteComponentProps<
  { invoiceId: string },
  any,
  { paymentMethod?: "manual" }
>;

export const ClientPaymentsInvoicePaymentSuccess: React.FC<Props> = ({
  match,
  location
}) => {
  const sessionQuery = useSession();
  const invoiceId = match.params.invoiceId;
  const isGuest = sessionQuery.data?.sessionType !== SessionType.user;
  const { openModal } = useModalContext();
  const userId = useUserId();
  const userQuery = useUserProfile(userId, {
    retry: false
  });
  const memberQuery = useMemberProfile(userId, {
    enabled: !isGuest,
    retry: false
  });
  const clientInvoiceQuery = useClientInvoiceQuery(invoiceId, {
    refetchInterval: 5000
  });
  const [updateClientInvoice, updateClientInvoiceMeta] = useUpdateClientInvoice(
    invoiceId
  );
  const history = useHistory();
  const downloadPdf = useDownloadPdf();

  const timePassedAfterPayment =
    new Date().getTime() -
    (
      clientInvoiceQuery.data?.events.paidAt ||
      clientInvoiceQuery.data?.events.paymentInTransitAt ||
      new Date()
    ).getTime();

  return (
    <>
      <ClientInvoiceWrapper invoiceId={invoiceId}>
        {clientInvoice => (
          <WSElement className={styles.wrapper}>
            <Header
              companyName={clientInvoice.memberCompany}
              companyLogoUrl={clientInvoice.memberLogoUrl}
              customDesktopAction={
                clientInvoice.attachments?.receiptPdf ? (
                  <WSButton.Secondary
                    small
                    onClick={() => {
                      if (clientInvoice.attachments?.receiptPdf) {
                        downloadPdf(clientInvoice.attachments.receiptPdf);
                      }
                    }}
                  >
                    Download receipt
                  </WSButton.Secondary>
                ) : (
                  <Tooltip
                    trigger={
                      <WSButton.Secondary small disabled>
                        Download receipt
                      </WSButton.Secondary>
                    }
                    showInfoIcon={false}
                    toolTipClassName={styles.toolTipContent}
                    arrowClassName={styles.arrow}
                  >
                    {timePassedAfterPayment > 120000 ? (
                      <WSText.Label>
                        Please check your email for the receipt
                      </WSText.Label>
                    ) : (
                      <WSText.Label>
                        Generating receipt, will appear here soon
                      </WSText.Label>
                    )}
                  </Tooltip>
                )
              }
            />
            <WSMessageBox.Success className={styles.successMessage}>
              <WSFlexBox.Center>
                <WSIcon name="successMark" mr="S" color="green500" />
                <WSText>Payment scheduled successfully.</WSText>
              </WSFlexBox.Center>
            </WSMessageBox.Success>

            <WSElement as="main" className={styles.main}>
              {clientInvoice.status === InvoiceStatus.PaymentInTransit ||
              location.state?.paymentMethod === "manual" ? (
                <>
                  <WSIcon name="clock" size="L" mb="S" />
                  <WSText.Heading3 mb="XS" data-testid="sentText">
                    We're waiting on your wire to arrive.
                  </WSText.Heading3>
                  <WSText mb="M" color="grey600">
                    If you haven't scheduled the wire at your bank yet, don't
                    forget to do that soon! Here are the details:
                  </WSText>

                  <Card mb="XL" className={styles.manualTransferDetailsCard}>
                    <InfoRow
                      label="Bank name"
                      value={clientInvoice.bankTransferInfo?.bankName || "N/A"}
                      longName
                      mb="S"
                    />
                    <InfoRow
                      label="Routing"
                      value={
                        clientInvoice.bankTransferInfo?.routingNumber || "N/A"
                      }
                      longName
                      mb="S"
                    />
                    <InfoRow
                      label="Account"
                      value={
                        clientInvoice.bankTransferInfo?.accountNumber || "N/A"
                      }
                      longName
                      mb="S"
                    />
                    <InfoRow
                      label="SWIFT"
                      value={clientInvoice.bankTransferInfo?.swiftCode || "N/A"}
                      longName
                      mb="S"
                    />
                    <InfoRow
                      label="Note/memo"
                      value={`Invoice #${clientInvoice.invoiceNumber || "N/A"}`}
                      longName
                    />
                  </Card>

                  <WSText.Heading4 mb="S">
                    Want to pay with a credit card or bank debit instead of
                    wire?
                  </WSText.Heading4>
                  <WSText mb="M">
                    You chose to pay this invoice with a wire transfer to the
                    account number above. If you're changed your mind, you can
                    still pay this invoice a different way.
                  </WSText>
                  <WSButton
                    loading={updateClientInvoiceMeta.isLoading}
                    onClick={() => {
                      updateClientInvoice(
                        {
                          status: InvoiceStatus.Open
                        },
                        {
                          onSuccess: () => {
                            queryCache.removeQueries([
                              QUERY_CLIENT_INVOICE,
                              invoiceId
                            ]);
                            history.push(`/payment/${invoiceId}/2`);
                          }
                        }
                      );
                    }}
                  >
                    Pay a different way
                  </WSButton>
                </>
              ) : (
                <>
                  <WSImage
                    src={handsUpImage}
                    placeholder={<></>}
                    width={60}
                    mb="M"
                  />
                  <WSText.Heading3 mb="XS" data-testid="sentText">
                    You sent {formatMoney(clientInvoice.amount)} to{" "}
                    {getClientInvoiceMemberName(clientInvoice)} at{" "}
                    {clientInvoice.memberCompany}.
                  </WSText.Heading3>
                  <WSText mb="L" color="grey600">
                    We’ve sent confirmation emails to both of you.
                  </WSText>

                  {isGuest ? (
                    <>
                      <WSText.Heading4>
                        Work with {clientInvoice.memberCompany} often?
                      </WSText.Heading4>
                      <WSText.Heading4 mb="M">
                        Create a free Wingspan account and get...
                      </WSText.Heading4>

                      <List
                        items={[
                          "Secure, 1-click payments",
                          "Manage multiple freelancers",
                          "Automated tax documents"
                        ]}
                        icon="successMark"
                        mb="L"
                      />

                      <WSButtons>
                        <WSButton
                          onClick={() => {
                            openModal(CLIENT_SIGN_IN_MODAL_KEY, {
                              clientEmailToPrefill:
                                clientInvoice.memberClient.emailTo,
                              signUpAsMember: true,
                              memberId: clientInvoice.memberClient.memberId,
                              member: clientInvoice.memberClient.member
                            });
                          }}
                        >
                          Create free account
                        </WSButton>
                        <WSButton.Secondary
                          onClick={() => {
                            const anonymousId = getSegmentAnonymousIdFromLocalStorage();
                            const name = clientInvoice.memberClient.name;
                            const email = clientInvoice.memberClient.emailTo;

                            const typeFormReference = typeformEmbed.makePopup(
                              `https://wingspan.typeform.com/to/caVyQaKi#name=${name}&email=${email}&anonymousid=${anonymousId}`,
                              {
                                onSubmit: event => {
                                  track("Client Requested a Demo", {
                                    responseId: event.response_id
                                  });
                                }
                              }
                            );
                            typeFormReference.open();
                          }}
                        >
                          Request a demo
                        </WSButton.Secondary>
                      </WSButtons>
                    </>
                  ) : memberQuery.data ? (
                    <>
                      <WSText.Heading4>
                        Know someone who might enjoy Wingspan?
                      </WSText.Heading4>
                      <WSText.Heading4 mb="M">
                        Share with colleagues and friends for...
                      </WSText.Heading4>

                      <List
                        items={[
                          "Secure, 1-click payments",
                          "Manage multiple freelancers",
                          "Automated tax documents"
                        ]}
                        icon="successMark"
                        mb="L"
                      />

                      <WSButton
                        onClick={() => {
                          openModal(REFERRALS_MODAL, {
                            customReferralCode:
                              userQuery.data?.tag ||
                              (clientInvoice.memberClient.name || "").replace(
                                new RegExp("[^a-zA-Z]", "g"),
                                ""
                              ) ||
                              clientInvoice.memberClient.emailTo
                          });
                        }}
                      >
                        Share Wingspan
                      </WSButton>
                    </>
                  ) : (
                    <>
                      <WSText.Heading4>
                        Do you want to maximize your cashflow?
                      </WSText.Heading4>
                      <WSText.Heading4 mb="M">
                        Create a free Wingspan account and get...
                      </WSText.Heading4>

                      <List
                        items={[
                          "Secure, 1-click payments",
                          "Manage multiple freelancers",
                          "Automated tax documents"
                        ]}
                        icon="successMark"
                        mb="L"
                      />

                      <WSButtons>
                        <WSButton
                          onClick={() => {
                            const anonymousId = getSegmentAnonymousIdFromLocalStorage();
                            const {
                              email,
                              profile: { firstName, lastName }
                            } = userQuery.data as INewUser;
                            const name = `${firstName} ${lastName}`;
                            const typeFormReference = typeformEmbed.makePopup(
                              `https://wingspan.typeform.com/to/caVyQaKi#name=${name}&email=${email}&userid=${userId}&anonymousid=${anonymousId}`,
                              {
                                onSubmit: event => {
                                  track("Client Requested a Demo", {
                                    responseId: event.response_id
                                  });
                                }
                              }
                            );
                            typeFormReference.open();
                          }}
                        >
                          Request a demo
                        </WSButton>
                        <WSButton.Secondary
                          onClick={() => {
                            history.push("/member/onboarding/customizing");
                          }}
                        >
                          Explore Wingspan
                        </WSButton.Secondary>
                      </WSButtons>
                    </>
                  )}
                </>
              )}
            </WSElement>
          </WSElement>
        )}
      </ClientInvoiceWrapper>
      <ModalReferrals />
    </>
  );
};
